import api, { handleResponse, handleError } from './api';

const applicationService = {
  /**
   * Get all applications for the current user (as a guest)
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getMyApplications() {
    try {
      const response = await api.get('/applications/me');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get the current user's application for a specific event
   * @param {string} eventId - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getMyApplicationForEvent(eventId) {
    try {
      const response = await api.get(`/applications/event/${eventId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get a single application by ID
   * @param {string} id - Application ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getApplication(id) {
    try {
      const response = await api.get(`/applications/${id}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get all applications for a specific event (as a host)
   * @param {string} eventId - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getEventApplications(eventId) {
    try {
      const response = await api.get(`/events/${eventId}/applications`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Create a new application for an event
   * @param {string} eventId - Event ID
   * @param {object} data - Application data
   * @param {string} [data.message] - Optional message to the host
   * @param {string} [data.plusOne] - Plus one guest name if applicable
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async createApplication(eventId, data) {
    try {
      const response = await api.post(`/events/${eventId}/applications`, data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Accept an application (as a host)
   * @param {string} id - Application ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async acceptApplication(id) {
    try {
      const response = await api.put(`/applications/${id}/accept`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Decline an application (as a host)
   * @param {string} id - Application ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async declineApplication(id) {
    try {
      const response = await api.put(`/applications/${id}/decline`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Withdraw an application (as a guest)
   * @param {string} id - Application ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async withdrawApplication(id) {
    try {
      const response = await api.delete(`/applications/${id}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Reinvite a declined guest (as a host) — deletes the application so guest can re-apply
   * @param {string} id - Application ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async reinviteApplication(id) {
    try {
      const response = await api.put(`/applications/${id}/reinvite`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Mark attendance for a guest (as a host)
   * @param {string} id - Application ID
   * @param {string} attendanceStatus - 'attended' or 'no_show'
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async markAttendance(id, attendanceStatus) {
    try {
      const response = await api.put(`/applications/${id}/attendance`, { attendanceStatus });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Host action — nudge a guest stuck in pending_payment via email + in-app
   * notification. Rate-limited server-side to 1×/24h per application.
   * @returns {Promise<{success: boolean, data: { sentAt: string }, error: string|null}>}
   */
  async sendPaymentReminder(id) {
    try {
      const response = await api.post(`/applications/${id}/payment-reminder`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Guest action — change quantity on their own pending_payment application
   * before completing checkout. Server validates ownership / status / capacity.
   */
  async updateQuantity(id, quantity) {
    try {
      const response = await api.put(`/applications/${id}/quantity`, { quantity });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
};

export default applicationService;
